import { StudentDetail, FeeStatementRow } from '../types';
import { apiClient } from './apiClient';
import { API_ENDPOINTS, API_CONFIG } from '../config/api';

// Fallback to mock data if API is unavailable
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

interface DbStudent {
  student_code: string;
  student_name: string;
  student_id: string;
  class_name: string;
  class_code: string;
  section_name: string;
  year_no: number;
  academic_year: string;
  father_name?: string;
  mother_name?: string;
  mobile_no?: string;
  email?: string;
  display_name?: string;
  sub_acct_code?: string;
}

interface DbClass {
  class_code: string;
  class_name: string;
  seq_id: number;
}

interface DbAcademicYear {
  academic_year: string;
}

// Mock data for fallback
const mockStudentsData: StudentDetail[] = [
  {
    studentCode: 'STU001',
    studentName: 'Aarav Singh',
    fatherName: 'Rajesh Singh',
    standard: '10',
    class: 'A',
    academicYear: '2025-2026',
    feeDetails: {
      studentCode: 'STU001',
      studentName: 'Aarav Singh',
      fatherName: 'Rajesh Singh',
      standard: '10',
      feeAmount: 45000,
      dueDate: '2026-03-31',
      paidAmount: 15000,
      remainingAmount: 30000,
    },
  },
];

// Helper function to map DB student to StudentDetail
const mapDbStudentToStudentDetail = (dbStudent: DbStudent): StudentDetail => {
  return {
    studentCode: dbStudent.student_code?.toString() || '',
    studentName: dbStudent.student_name || '',
    fatherName: dbStudent.father_name || '',
    standard: dbStudent.class_name || '',
    class: dbStudent.section_name || '',
    academicYear: dbStudent.academic_year || '',
    sub_acct_code: dbStudent.sub_acct_code,
    feeDetails: {
      studentCode: dbStudent.student_code?.toString() || '',
      studentName: dbStudent.student_name || '',
      fatherName: dbStudent.father_name || '',
      standard: dbStudent.class_name || '',
      feeAmount: 0,
      dueDate: '',
      paidAmount: 0,
      remainingAmount: 0,
    },
    fee_details: (dbStudent as any).fee_details, // Preserve API fee_details
  };
};

export const studentService = {
  // Fetch student by student code
  getStudentByCode: async (studentCode: string): Promise<StudentDetail | null> => {
    if (USE_MOCK_DATA) {
      const student = mockStudentsData.find(
        s => s.studentCode.toLowerCase() === studentCode.toLowerCase()
      );
      return student || null;
    }

    try {
      const response = await apiClient.get<DbStudent>(
        API_ENDPOINTS.STUDENT_BY_CODE(studentCode)
      );

      if (response.success && response.data) {
        return mapDbStudentToStudentDetail(response.data);
      }

      return null;
    } catch (error) {
      console.error('Error fetching student by code:', error);
      return null;
    }
  },

  // Fetch student by student ID (e.g., 13/18)
  getStudentById: async (studentId: string): Promise<StudentDetail | null> => {
    if (USE_MOCK_DATA) {
      const student = mockStudentsData.find(
        s => s.studentCode.toLowerCase() === studentId.toLowerCase()
      );
      return student || null;
    }

    try {
      const response = await apiClient.get<DbStudent>(
        API_ENDPOINTS.STUDENT_BY_ID(studentId)
      );

      if (response.success && response.data) {
        return mapDbStudentToStudentDetail(response.data);
      }

      return null;
    } catch (error) {
      console.error('Error fetching student by ID:', error);
      return null;
    }
  },

  // Search students by query
  searchStudents: async (classCode: string, query: string): Promise<StudentDetail[]> => {
    if (USE_MOCK_DATA) {
      return mockStudentsData.filter(s =>
        s.studentName.toLowerCase().includes(query.toLowerCase()) ||
        s.studentCode.toLowerCase().includes(query.toLowerCase())
      );
    }

    try {
      const response = await apiClient.get<DbStudent[]>(
        API_ENDPOINTS.SEARCH_STUDENTS(classCode, query)
      );

      if (response.success && response.data) {
        return response.data.map(mapDbStudentToStudentDetail);
      }

      return [];
    } catch (error) {
      console.error('Error searching students:', error);
      return [];
    }
  },

  // Get students by class code
  getStudentsByClass: async (classCode: string): Promise<StudentDetail[]> => {
    if (USE_MOCK_DATA) {
      return mockStudentsData;
    }

    try {
      const response = await apiClient.get<DbStudent[]>(
        API_ENDPOINTS.STUDENTS_BY_CLASS(classCode)
      );

      if (response.success && response.data) {
        return response.data.map(mapDbStudentToStudentDetail);
      }

      return [];
    } catch (error) {
      console.error('Error fetching students by class:', error);
      return [];
    }
  },

  // Get all classes
  getAllClasses: async (): Promise<DbClass[]> => {
    if (USE_MOCK_DATA) {
      return [
        { class_code: 'CLS001', class_name: 'Class 10', seq_id: 1 },
        { class_code: 'CLS002', class_name: 'Class 9', seq_id: 2 },
      ];
    }

    try {
      const response = await apiClient.get<DbClass[]>(API_ENDPOINTS.CLASSES);

      if (response.success && response.data) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching classes:', error);
      return [];
    }
  },

  // Get sections by class code
  getSectionsByClass: async (classCode: string): Promise<string[]> => {
    if (USE_MOCK_DATA) {
      return ['A', 'B', 'C'];
    }

    try {
      const response = await apiClient.get<{ section_name: string }[]>(
        API_ENDPOINTS.SECTIONS(classCode)
      );

      if (response.success && response.data) {
        return response.data.map(s => s.section_name);
      }

      return [];
    } catch (error) {
      console.error('Error fetching sections:', error);
      return [];
    }
  },

  // Get all academic years
  getAcademicYears: async (): Promise<string[]> => {
    if (USE_MOCK_DATA) {
      return ['2025-2026', '2024-2025'];
    }

    try {
      const response = await apiClient.get<DbAcademicYear[]>(
        API_ENDPOINTS.ACADEMIC_YEARS
      );

      if (response.success && response.data) {
        return response.data.map(y => y.academic_year);
      }

      return [];
    } catch (error) {
      console.error('Error fetching academic years:', error);
      return [];
    }
  },

  // Legacy methods for backward compatibility
  getStudentByDetails: async (
    academicYear: string,
    standard: string,
    className: string,
    studentName: string
  ): Promise<StudentDetail | null> => {
    if (USE_MOCK_DATA) {
      const student = mockStudentsData.find(
        s =>
          s.academicYear === academicYear &&
          s.standard === standard &&
          s.class === className &&
          s.studentName.toLowerCase() === studentName.toLowerCase()
      );
      return student || null;
    }

    // For real API, we would need to implement a specific endpoint
    // For now, return null or implement a workaround
    return null;
  },

  getStudentNamesByDetails: async (
    academicYear: string,
    standard: string,
    className: string
  ): Promise<string[]> => {
    if (USE_MOCK_DATA) {
      const students = mockStudentsData.filter(
        s =>
          s.academicYear === academicYear &&
          s.standard === standard &&
          s.class === className
      );
      return [...new Set(students.map(s => s.studentName))];
    }

    return [];
  },

  getStandardsByAcademicYear: async (academicYear: string): Promise<string[]> => {
    if (USE_MOCK_DATA) {
      const students = mockStudentsData.filter(
        s => s.academicYear === academicYear
      );
      return [...new Set(students.map(s => s.standard))];
    }

    return [];
  },

  getClassesByAcademicYearAndStandard: async (
    academicYear: string,
    standard: string
  ): Promise<string[]> => {
    if (USE_MOCK_DATA) {
      const students = mockStudentsData.filter(
        s => s.academicYear === academicYear && s.standard === standard
      );
      return [...new Set(students.map(s => s.class))];
    }

    return [];
  },

  // Get tuition fee statement for a student
  getTuitionFeeStatement: async (
    subAcctCode: string,
    academicYear?: number,
    financeYear?: number,
    className?: string
  ): Promise<FeeStatementRow[]> => {
    try {
      const params = new URLSearchParams();
      if (academicYear) params.append('academicYear', academicYear.toString());
      if (financeYear) params.append('financeYear', financeYear.toString());
      if (className) params.append('className', className);

      const url = `${API_ENDPOINTS.TUITION_STATEMENT(subAcctCode)}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<FeeStatementRow[]>(url);

      if (response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }

      return [];
    } catch (error) {
      console.error('Error fetching tuition fee statement:', error);
      return [];
    }
  },

  // Get transportation fee statement for a student
  getTransportationFeeStatement: async (
    subAcctCode: string,
    academicYear?: number,
    financeYear?: number,
    className?: string
  ): Promise<FeeStatementRow[]> => {
    try {
      const params = new URLSearchParams();
      if (academicYear) params.append('academicYear', academicYear.toString());
      if (financeYear) params.append('financeYear', financeYear.toString());
      if (className) params.append('className', className);

      const url = `${API_ENDPOINTS.TRANSPORTATION_STATEMENT(subAcctCode)}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<FeeStatementRow[]>(url);

      if (response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }

      return [];
    } catch (error) {
      console.error('Error fetching transportation fee statement:', error);
      return [];
    }
  },

  // Export tuition fee statement as PDF
  exportTuitionStatementPDF: async (
    subAcctCode: string,
    academicYear?: number,
    financeYear?: number
  ): Promise<void> => {
    try {
      const params = new URLSearchParams();
      if (academicYear) params.append('academicYear', academicYear.toString());
      if (financeYear) params.append('financeYear', financeYear.toString());

      const url = `${API_CONFIG.BASE_URL}/students/${subAcctCode}/export-tuition-pdf${params.toString() ? `?${params.toString()}` : ''}`;
      
      // Fetch the PDF as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to export tuition statement PDF');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `tuition-statement-${subAcctCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting tuition statement PDF:', error);
      throw error;
    }
  },

  // Export transportation fee statement as PDF
  exportTransportationStatementPDF: async (
    subAcctCode: string,
    academicYear?: number,
    financeYear?: number
  ): Promise<void> => {
    try {
      const params = new URLSearchParams();
      if (academicYear) params.append('academicYear', academicYear.toString());
      if (financeYear) params.append('financeYear', financeYear.toString());

      const url = `${API_CONFIG.BASE_URL}/students/${subAcctCode}/export-transportation-pdf${params.toString() ? `?${params.toString()}` : ''}`;
      
      // Fetch the PDF as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to export transportation statement PDF');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `transportation-statement-${subAcctCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting transportation statement PDF:', error);
      throw error;
    }
  },
};
